import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Search, X, Loader2, ChevronDown } from 'lucide-react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import Avatar from './Avatar';
import PersonCell, { splitFullName } from './PersonCell';
import { isPersonOption, personPicture } from './personOption';

const AsyncGenericSelect = ({
    value,
    onChange,
    onSearch,
    onGetById,
    renderOption,
    label = '',
    placeholder,
    minChars = 1,
    direction = 'bottom',
    isMulti = false,
    required = false,
    error,
    style = {},
    disabled = false,
    busy = false,
    // Kompakt varyant: filtre çubukları gibi dar alanlar için — etiket
    // placeholder'a düşer, yükseklik/padding/font küçülür.
    compact = false,
    // Açılır listedeki arama kutusunun ipucu — neyle arandığını söyler (ör. pozisyon
    // seçicide "ünvan, departman, kişi veya kod"). Verilmezse genel "Ara...".
    searchPlaceholder,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedLabel, setSelectedLabel] = useState('');
    // Tekli seçimde metnin yanında avatarı da çizebilmek için seçilen NESNE
    // saklanır — yalnız etiket string'i tutulduğunda fotoğraf kayboluyordu.
    const [selectedItem, setSelectedItem] = useState(null);
    const [dropdownStyle, setDropdownStyle] = useState(null);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);
    // The single-select value that the current selectedLabel was resolved for.
    // Lets us skip a redundant onGetById right after a manual pick, while STILL
    // re-fetching when the value is changed externally (e.g. a deriveFields
    // auto-fill that updates the field on every employee re-selection).
    const labelValueRef = useRef(null);

    const formatLabel = useCallback((data) => {
        if (!data) return '';
        if (data.label) return data.label;
        if (data.firstName || data.lastName)
            return `${data.firstName || ''} ${data.lastName || ''}`.trim();
        return data.name || data.title || data.fullName || '';
    }, []);

    // Sync selected value from outside
    useEffect(() => {
        let isMounted = true;
        const loadInitialData = async () => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
                if (isMulti) setSelectedItems([]);
                else { setSelectedLabel(''); setSelectedItem(null); }
                labelValueRef.current = null;
                return;
            }
            const currentIds = isMulti ? (value || []) : [value];
            if (isMulti && currentIds.length === selectedItems.length) return;
            // Skip the fetch only when the existing label already belongs to THIS
            // value (e.g. just set by a manual pick). If the value changed from
            // outside, the label is stale and must be re-resolved.
            if (!isMulti && selectedLabel && String(labelValueRef.current) === String(value)) return;

            try {
                if (onGetById) {
                    if (isMulti) {
                        const promises = currentIds.map((id) => onGetById(id));
                        const results = await Promise.all(promises);
                        if (isMounted) setSelectedItems(results.filter(Boolean));
                    } else {
                        const res = await onGetById(value);
                        if (isMounted && res) {
                            setSelectedLabel(formatLabel(res));
                            setSelectedItem(res);
                            labelValueRef.current = value;
                        }
                    }
                }
            } catch (err) {
                console.error('AsyncSelect Load Error:', err);
            }
        };
        loadInitialData();
        return () => { isMounted = false; };
    }, [value, onGetById, isMulti, formatLabel]);

    // Search debounce — leading: dropdown açılışında anında sorgu; trailing: yazım bitince son sorgu.
    // seq guard: eski bir yanıtın daha yeni sorgunun sonucunu ezmesini önler.
    const seqRef = useRef(0);
    const fetchOptions = useMemo(
        () =>
            debounce(async (query) => {
                const seq = ++seqRef.current;
                setLoading(true);
                try {
                    const results = await onSearch(query);
                    if (seq === seqRef.current) setOptions(results || []);
                } catch {
                    if (seq === seqRef.current) setOptions([]);
                } finally {
                    if (seq === seqRef.current) setLoading(false);
                }
            }, 500, { leading: true, trailing: true }),
        [onSearch]
    );

    useEffect(() => {
        if (isOpen) fetchOptions(searchTerm);
    }, [searchTerm, isOpen, fetchOptions]);

    // Auto-flip: aşağıda yer yoksa yukarı aç. `direction='top'` zorla yukarı.
    useLayoutEffect(() => {
        if (!isOpen) { setDropdownStyle(null); return; }
        const updatePosition = () => {
            if (!triggerRef.current) return;
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownHeight = dropdownRef.current?.offsetHeight || 320;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            let top;
            if (direction === 'top' && spaceAbove >= dropdownHeight + 8) {
                top = rect.top - dropdownHeight - 4;
            } else if (spaceBelow >= dropdownHeight + 8) {
                top = rect.bottom + 4;
            } else if (spaceAbove >= dropdownHeight + 8) {
                top = rect.top - dropdownHeight - 4;
            } else {
                top = Math.max(8, window.innerHeight - dropdownHeight - 8);
            }
            setDropdownStyle({
                position: 'fixed',
                top,
                left: rect.left,
                width: rect.width,
                zIndex: 99999,
            });
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, options.length, direction]);

    // Click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        if (isMulti) {
            const currentValues = Array.isArray(value) ? value : [];
            const isSelected = currentValues.includes(option.id);
            let nextValue;
            if (isSelected) {
                nextValue = currentValues.filter((id) => id !== option.id);
                setSelectedItems((prev) => prev.filter((item) => item.id !== option.id));
            } else {
                nextValue = [...currentValues, option.id];
                setSelectedItems((prev) => [...prev, option]);
            }
            onChange(nextValue);
            setSearchTerm('');
            setTimeout(() => searchInputRef.current?.focus(), 0);
        } else {
            onChange(option.id);
            setSelectedLabel(formatLabel(option));
            setSelectedItem(option);
            labelValueRef.current = option.id;
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const removeBadge = (e, id) => {
        e.stopPropagation();
        const nextValue = value.filter((valId) => valId !== id);
        setSelectedItems((prev) => prev.filter((item) => item.id !== id));
        onChange(nextValue);
        if (isOpen) searchInputRef.current?.focus();
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(isMulti ? [] : null);
        if (isMulti) setSelectedItems([]);
        else { setSelectedLabel(''); setSelectedItem(null); }
        labelValueRef.current = null;
    };

    const active = isOpen;
    const hasValue = isMulti ? value?.length > 0 : !!value;
    // Kompakt modda etiket ayrı satır kaplamaz; placeholder verilmemişse
    // etiket metni placeholder olarak kullanılır.
    const effectivePlaceholder = compact && (placeholder == null || placeholder === '')
        ? label
        : placeholder;

    return (
        <OuterWrapper ref={triggerRef} style={style}>
            {!compact && (
                <FixedLabel $active={active} $error={!!error}>
                    {label}
                    {required && <RequiredMark>*</RequiredMark>}
                </FixedLabel>
            )}

            <SelectBox onClick={() => { if (!disabled) setIsOpen(!isOpen); }} $active={active} $error={!!error} $isMulti={isMulti} $disabled={disabled} $compact={compact}>
                <ValueContainer>
                    {isMulti ? (
                        <BadgeWrapper>
                            {selectedItems.map((item) => {
                                const itemLabel = formatLabel(item);
                                const parts = splitFullName(itemLabel);
                                return (
                                    <Badge key={item.id}>
                                        {isPersonOption(item) && (
                                            <Avatar
                                                src={personPicture(item) || undefined}
                                                firstName={parts.firstName}
                                                lastName={parts.lastName}
                                                variant="circle"
                                                size={18}
                                            />
                                        )}
                                        {itemLabel}
                                        <BadgeRemove onClick={(e) => removeBadge(e, item.id)}>
                                            <X size={12} />
                                        </BadgeRemove>
                                    </Badge>
                                );
                            })}
                            {(!value || value.length === 0) && effectivePlaceholder !== '' && (
                                <Placeholder $compact={compact}>{effectivePlaceholder ?? `${t('common.choose', { defaultValue: 'Seçin' })}...`}</Placeholder>
                            )}
                        </BadgeWrapper>
                    ) : selectedLabel && isPersonOption(selectedItem) ? (
                        // Seçili kişi de listedeki gibi avatarlı görünür — aynı
                        // kişinin dropdown'da fotoğraflı, seçildikten sonra düz
                        // metin olması tutarsızlıktı.
                        <PersonCell
                            name={selectedLabel}
                            profilePicture={personPicture(selectedItem)}
                            variant="circle"
                            size={compact ? 20 : 24}
                            style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: compact ? 13 : 14, color: '#004242' }}
                        />
                    ) : (
                        <ValueText $isPlaceholder={!selectedLabel} $compact={compact}>
                            {selectedLabel || (effectivePlaceholder === '' ? '' : (effectivePlaceholder ?? `${t('common.choose', { defaultValue: 'Seçin' })}...`))}
                        </ValueText>
                    )}
                </ValueContainer>

                <IconGroup>
                    {busy && <LoaderIcon size={16} />}
                    {!busy && hasValue && !disabled && (
                        <ClearButton onClick={handleClear}>
                            <X size={14} />
                        </ClearButton>
                    )}
                    {!busy && (
                        <ChevronDown size={16} color="#A0A8B4" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                    )}
                </IconGroup>
            </SelectBox>

            {error && <ErrorText>{error}</ErrorText>}

            {isOpen && !disabled && createPortal(
                // data-portal-layer: bu liste document.body'ye portal'lanır, yani onu açan panelin
                // DOM alt ağacının dışında kalır. "Dışarı tıklayınca kapan" kalıbını kullanan
                // ekranlar (utils/isInsidePortalLayer) buradaki tıklamayı "dışarısı" saymamalı —
                // aksi halde panel kapanır, seçenek unmount olur ve tıklama hiç işlemez.
                <Dropdown ref={dropdownRef} data-portal-layer="async-select" style={dropdownStyle || { position: 'fixed', visibility: 'hidden' }}>
                    <SearchWrapper>
                        <Search size={15} color="#A0A8B4" />
                        <SearchInput
                            autoFocus
                            ref={searchInputRef}
                            placeholder={searchPlaceholder || `${t('common.search', { defaultValue: 'Ara' })}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {loading && <LoaderIcon size={15} />}
                    </SearchWrapper>

                    <List>
                        {options.length > 0 ? (
                            options.map((opt) => (
                                <ListItem
                                    key={opt.id}
                                    onClick={() => handleSelect(opt)}
                                    $isSelected={isMulti ? value?.includes(opt.id) : value === opt.id}
                                >
                                    {renderOption ? renderOption(opt) : isPersonOption(opt) ? (
                                        <PersonCell
                                            name={formatLabel(opt)}
                                            profilePicture={personPicture(opt)}
                                            variant="circle"
                                            size={28}
                                        />
                                    ) : (
                                        <span>{formatLabel(opt)}</span>
                                    )}
                                </ListItem>
                            ))
                        ) : (
                            <NoResult>
                                {loading
                                    ? t('common.loading')
                                    : t('common.noResults', { defaultValue: 'Sonuç bulunamadı' })}
                            </NoResult>
                        )}
                    </List>
                </Dropdown>,
                document.body
            )}
        </OuterWrapper>
    );
};

export default AsyncGenericSelect;

const OuterWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: ${(p) => (p.$active ? 9999 : 1)};
`;

const FixedLabel = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  padding: 0 4px;
  background-color: #fff;
  color: ${(p) => (p.$error ? '#E53935' : p.$active ? '#0291EF' : '#A0A8B4')};
  z-index: 2;
  pointer-events: none;
  transition: color 0.2s ease;
`;

const RequiredMark = styled.span`
  color: #E53935;
  margin-left: 2px;
`;

// #72 (UI düzeltme demeti T6) — "ince varyant" (GenericSelect.js/TextInput
// .jsx'in AYNI gerekçesi): taban (compact OLMAYAN) yükseklik 48px→40px,
// çoklu-seçim dolgusu 6px 14px→4px 12px. `compact` prop'u ZATEN vardı
// (filtre çubukları için 34px) — DEĞİŞTİRİLMEDİ.
const SelectBox = styled.div`
  min-height: ${(p) => (p.$compact ? '34px' : '40px')};
  height: ${(p) => (p.$isMulti ? 'auto' : p.$compact ? '34px' : '40px')};
  padding: ${(p) => (p.$isMulti
    ? (p.$compact ? '3px 10px' : '4px 12px')
    : (p.$compact ? '0 10px' : '0 14px'))};
  border: 1.5px solid ${(p) => (p.$error ? '#E53935' : p.$active ? '#0291EF' : '#E0E4E8')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  background-color: ${(p) => (p.$disabled ? '#F5F7FA' : '#fff')};
  opacity: ${(p) => (p.$disabled ? 0.7 : 1)};
  gap: 8px;
  box-shadow: ${(p) => (p.$active ? '0 0 0 3px rgba(2, 145, 239, 0.1)' : 'none')};
  transition: all 0.2s;

  &:hover {
    border-color: ${(p) => (p.$disabled ? '#E0E4E8' : p.$error ? '#E53935' : p.$active ? '#0291EF' : '#C0C8D0')};
  }
`;

const ValueContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

const BadgeWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 2px 0;
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: rgba(2, 145, 239, 0.06);
  color: #004242;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #E0E4E8;
`;

const BadgeRemove = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #A0A8B4;
  &:hover { color: #E53935; }
`;

const Placeholder = styled.span`
  color: #A0A8B4;
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${(p) => (p.$compact ? '13px' : '14px')};
`;

const ValueText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${(p) => (p.$compact ? '13px' : '14px')};
  color: ${(p) => (p.$isPlaceholder ? '#A0A8B4' : '#004242')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #A0A8B4;
  flex-shrink: 0;
`;

const ClearButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover { color: #E53935; }
`;

const Dropdown = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 66, 66, 0.12);
  overflow: hidden;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #E8ECF0;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #004242;
  background: transparent;
  &::placeholder { color: #A0A8B4; }
`;

const List = styled.div`
  max-height: 240px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #E0E4E8; border-radius: 4px; }
`;

const ListItem = styled.div`
  padding: 9px 14px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #004242;
  background-color: ${(p) => (p.$isSelected ? 'rgba(2, 145, 239, 0.06)' : 'transparent')};
  &:hover { background-color: rgba(2, 145, 239, 0.04); }
`;

const NoResult = styled.div`
  padding: 16px;
  text-align: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #A0A8B4;
`;

const LoaderIcon = styled(Loader2)`
  color: #0291EF;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
  color: #E53935;
`;
